// src/utils/generateBusinessReportPDF.js
//
// Многостраничный PDF-отчёт по магазину с выбираемыми блоками:
// 1 Сводка / 2 Продажи / 3 Скидки / 4 Возвраты / 6 Закупки / 8 Изъятия /
// 11 Лояльность / 12 Топ товаров / 13 Категории / 14 Покупатели /
// 15 Сотрудники / 16 Налоговый.
//
// Использует jsPDF + autoTable + Roboto (кириллица).

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

let cachedFont = null;
async function loadCyrillicFont() {
  if (cachedFont) return cachedFont;
  try {
    const ttfUrl = 'https://cdn.jsdelivr.net/npm/roboto-font@0.1.0/fonts/Roboto/roboto-regular-webfont.ttf';
    const res = await fetch(ttfUrl);
    if (!res.ok) throw new Error('Font fetch failed');
    const buf = await res.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    cachedFont = btoa(binary);
    return cachedFont;
  } catch (e) {
    console.warn('Шрифт кириллицы не загружен:', e);
    return null;
  }
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDateTime(d) {
  return new Date(d).toLocaleString('ru', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
function fmtMoney(n) {
  return Math.round(Number(n) || 0).toLocaleString('ru');
}
// Количество/остаток: чистит ошибки плавающей точки (2.800000001 → 2.8)
function fmtNum(n) {
  return String(Math.round((Number(n) || 0) * 1000) / 1000);
}
function fmtMoney2(n) {
  return (Number(n) || 0).toFixed(2).replace('.', ',');
}
function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return (Number(n) >= 0 ? '+' : '') + Number(n).toFixed(1) + '%';
}

// Заголовок блока
function blockHeader(doc, title, y) {
  doc.setFontSize(13);
  doc.setTextColor(99, 102, 241); // brand-primary
  doc.text(title, 14, y);
  doc.setDrawColor(99, 102, 241);
  doc.setLineWidth(0.5);
  doc.line(14, y + 1.5, 196, y + 1.5);
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(10);
  return y + 8;
}

function addPageIfNeeded(doc, y, threshold = 270) {
  if (y >= threshold) {
    doc.addPage();
    return 20;
  }
  return y;
}

// Шапка отчёта
function renderHeader(doc, data, currency) {
  doc.setFontSize(18);
  doc.text('Отчёт по магазину', 14, 20);
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(`${data.shop.name}  ·  ${currency}`, 14, 27);
  doc.text(
    `Период: ${fmtDate(data.period.from)} — ${fmtDate(data.period.to)}`,
    14, 33
  );
  doc.text(`Сформирован: ${fmtDateTime(new Date())}`, 14, 39);
  doc.setTextColor(20);
  return 50;
}

// ─── Блок 1: Сводка ───────────────────────────────────────────
function renderSummary(doc, s, y, currency) {
  y = blockHeader(doc, 'Сводка за период', y);

  const hasReturns = !!(s.returns && s.returns.money > 0);

  const rows = hasReturns
    ? [
        ['Выручка (продажи)',            `${fmtMoney(s.revenueGross)} ${currency}`],
        ['Возвраты покупателям',         `−${fmtMoney(s.returns.money)} ${currency} (${s.returns.count} шт)`],
        ['Выручка за вычетом возвратов', `${fmtMoney(s.revenue)} ${currency}`],
        ['Себестоимость',                `${fmtMoney(s.cost)} ${currency}`],
        ['Прибыль (с учётом возвратов)', `${fmtMoney(s.profit)} ${currency}`],
        ['Маржа',                        `${s.margin.toFixed(1)} %`],
        ['Продаж',                       String(s.salesCount)],
        ['Средний чек',                  `${fmtMoney(s.avgCheck)} ${currency}`],
        ['Покупатели по карте', `${s.customers.total} (новых: ${s.customers.new}, вернулось: ${s.customers.returning})`],
      ]
    : [
        ['Выручка',         `${fmtMoney(s.revenue)} ${currency}`],
        ['Себестоимость',   `${fmtMoney(s.cost)} ${currency}`],
        ['Прибыль',         `${fmtMoney(s.profit)} ${currency}`],
        ['Маржа',           `${s.margin.toFixed(1)} %`],
        ['Продаж',          String(s.salesCount)],
        ['Средний чек',     `${fmtMoney(s.avgCheck)} ${currency}`],
        ['Покупатели по карте', `${s.customers.total} (новых: ${s.customers.new}, вернулось: ${s.customers.returning})`],
      ];

  if (s.comparison && s.comparison.prevRevenue > 0) {
    rows.push(['— Сравнение с предыдущим периодом —', '']);
    rows.push(['Изменение выручки', fmtPct(s.comparison.revenueDelta)]);
    rows.push(['Изменение прибыли', fmtPct(s.comparison.profitDelta)]);
    rows.push(['Продаж раньше', `${s.comparison.prevSalesCount} (тек: ${s.salesCount})`]);
    rows.push(['Средний чек раньше', `${fmtMoney(s.comparison.prevAvgCheck)} ${currency}`]);
  }

  autoTable(doc, {
    startY: y,
    head: [['Показатель', 'Значение']],
    body: rows,
    theme: 'grid',
    styles: { font: 'Roboto', fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'normal' },
    columnStyles: { 0: { cellWidth: 90 }, 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Предупреждение: товары с нулевой себестоимостью завышают прибыль.
  // Показываем, только если такие продажи были — чтобы не мозолить глаза.
  if (s.zeroCost && s.zeroCost.revenue > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(9);
    doc.setTextColor(180, 83, 9);
    doc.text(
      `Внимание: у ${s.zeroCost.count} поз. не указана себестоимость (выручка ${fmtMoney(s.zeroCost.revenue)} ${currency}).`,
      14, y,
    );
    y += 4;
    doc.text('Прибыль и маржа по ним считаются как 100% и завышают итог.', 14, y);
    doc.setTextColor(0, 0, 0);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Товар без себестоимости', 'Продано', 'Выручка']],
      body: s.zeroCost.items.map(x => [
        x.name,
        String(x.qty),
        `${fmtMoney(x.revenue)} ${currency}`,
      ]),
      theme: 'grid',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [217, 119, 6], textColor: 255, fontStyle: 'normal' },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // «Ноунэйм»: продажи товаров, которых нет в базе (свободная цена).
  // Себестоимость у них неизвестна — прибыль по ним завышена.
  if (s.noName && s.noName.lines > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(9);
    doc.setTextColor(180, 83, 9);
    doc.text(
      `Без карточки товара продано ${s.noName.lines} поз. на ${fmtMoney(s.noName.revenue)} ${currency} (себестоимость неизвестна).`,
      14, y,
    );
    doc.setTextColor(0, 0, 0);
    y += 4;
    if (s.noName.barcodes && s.noName.barcodes.length) {
      doc.text('Эти коды пробивали без карточки — их стоит завести в каталог первыми:', 14, y);
      y += 4;
      autoTable(doc, {
        startY: y,
        head: [['Штрихкод', 'Раз', 'Выручка']],
        body: s.noName.barcodes.map(x => [x.barcode, String(x.times), `${fmtMoney(x.revenue)} ${currency}`]),
        theme: 'grid',
        styles: { font: 'Roboto', fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [217, 119, 6], textColor: 255, fontStyle: 'normal' },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 6;
    } else {
      y += 2;
    }
  }

  // График выручки по дням (простой ASCII-like через autoTable)

  if (s.byDay && Object.keys(s.byDay).length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(10);
    doc.text('Выручка и прибыль по дням:', 14, y);
    y += 4;
    const dayRows = Object.entries(s.byDay)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([day, v]) => [day, String(v.count), `${fmtMoney(v.revenue)} ${currency}`, `${fmtMoney(v.profit || 0)} ${currency}`]);
    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Продаж', 'Выручка', 'Прибыль']],
      body: dayRows,
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Пиковые часы (топ-3)
  if (s.byHour) {
    const peak = Object.entries(s.byHour)
      .filter(([_, c]) => c > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    if (peak.length > 0) {
      y = addPageIfNeeded(doc, y);
      doc.setFontSize(10);
      doc.text(
        'Пиковые часы продаж: ' + peak.map(([h, c]) => `${h}:00 (${c})`).join(', '),
        14, y
      );
      y += 6;
    }
  }
  return y + 4;
}

// ─── Блок 12: Топ товаров ─────────────────────────────────────
function renderTopProducts(doc, t, y, currency) {
  y = blockHeader(doc, 'Топ товаров', y);

  doc.setFontSize(10);
  doc.text('Топ 10 по выручке:', 14, y);
  y += 3;
  autoTable(doc, {
    startY: y,
    head: [['№', 'Товар', 'Продано', 'Выручка', 'Прибыль']],
    body: t.byRevenue.map((p, i) => [
      i + 1, p.name, fmtNum(p.qty),
      `${fmtMoney(p.revenue)} ${currency}`,
      `${fmtMoney(p.profit)} ${currency}`,
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 0: { halign: 'center', cellWidth: 8 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (t.byQty.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(10);
    doc.text('Топ 10 по количеству:', 14, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [['№', 'Товар', 'Продано']],
      body: t.byQty.map((p, i) => [i + 1, p.name, fmtNum(p.qty)]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 0: { halign: 'center', cellWidth: 8 }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  if (t.lowStock && t.lowStock.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(10);
    doc.setTextColor(200, 50, 50);
    doc.text(`Критический остаток (${t.lowStock.length}):`, 14, y);
    doc.setTextColor(20);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [['Товар', 'Остаток']],
      body: t.lowStock.slice(0, 20).map(p => [p.name, fmtNum(p.stock)]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [255, 240, 240], textColor: 150 },
      columnStyles: { 1: { halign: 'right', cellWidth: 30 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  if (t.outsiders && t.outsiders.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(10);
    doc.text(`Не продавались за период (${t.outsiders.length}):`, 14, y);
    y += 3;
    const outRows = t.outsiders.slice(0, 30).map(p => [p.name, fmtNum(p.stock)]);
    autoTable(doc, {
      startY: y,
      head: [['Товар', 'Остаток']],
      body: outRows,
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'right', cellWidth: 30 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }
  return y + 4;
}

// ─── Блок 13: Категории ───────────────────────────────────────
function renderCategories(doc, c, y, currency) {
  y = blockHeader(doc, 'Категории', y);
  autoTable(doc, {
    startY: y,
    head: [['Категория', 'Продано', 'Выручка', 'Прибыль', 'Маржа', 'Доля']],
    body: c.list.map(cat => [
      cat.name, fmtNum(cat.qty),
      `${fmtMoney(cat.revenue)} ${currency}`,
      `${fmtMoney(cat.profit)} ${currency}`,
      `${cat.margin.toFixed(1)} %`,
      `${cat.share.toFixed(1)} %`,
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Услуги (отдельно от товаров) ────────────────────────────
function renderServices(doc, s, y, currency) {
  if (!s || !s.list || s.list.length === 0) return y;
  y = blockHeader(doc, 'Услуги', y);
  doc.setFont('Roboto', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
  doc.text(`Выручка по услугам: ${fmtMoney(s.total.revenue)} ${currency} · Прибыль: ${fmtMoney(s.total.profit)} ${currency} · Услуг: ${s.total.count}`, 14, y);
  doc.setTextColor(0);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [['Услуга', 'Кол-во', 'Выручка', 'Прибыль']],
    body: s.list.map(it => [
      it.name, fmtNum(it.qty),
      `${fmtMoney(it.revenue)} ${currency}`,
      `${fmtMoney(it.profit)} ${currency}`,
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 16: Налоговый ───────────────────────────────────────
// ─── Блок 17: Оптовые продажи ────────────────────────────────
function renderWholesale(doc, w, y, currency) {
  y = blockHeader(doc, 'Оптовые продажи (накладные)', y);
  doc.setFontSize(10);
  doc.text(
    `Выручка: ${fmtMoney(w.revenue)} ${currency}  ·  Прибыль: ${fmtMoney(w.profit)} ${currency}  ·  Накладных: ${w.invoicesCount}`,
    14, y,
  );
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    `Средняя накладная: ${fmtMoney(w.avgInvoice)} ${currency}  ·  Маржа: ${(w.margin || 0).toFixed(1)}%`,
    14, y,
  );
  doc.setTextColor(20);
  y += 6;

  if (w.comparison && (w.comparison.prevRevenue > 0 || w.comparison.prevInvoicesCount > 0)) {
    const c = w.comparison;
    const dR = c.revenueDelta;
    const dP = c.profitDelta;
    doc.setFontSize(8);
    doc.setTextColor(120);
    const delta = (v) => v == null ? '—' : (v >= 0 ? `+${v.toFixed(1)}%` : `${v.toFixed(1)}%`);
    doc.text(
      `Пред. период: выручка ${fmtMoney(c.prevRevenue)} (${delta(dR)})  ·  накладных ${c.prevInvoicesCount}  ·  прибыль ${fmtMoney(c.prevProfit)} (${delta(dP)})`,
      14, y,
    );
    doc.setTextColor(20);
    y += 6;
  }

  if (w.invoicesCount === 0) {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Оптовых отгрузок за период не было', 14, y);
    doc.setTextColor(20);
    return y + 8;
  }

  // Топ оптовых клиентов
  if (Array.isArray(w.topClients) && w.topClients.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Клиент', 'Накладных', 'Выручка']],
      body: w.topClients.map(c => [
        c.name,
        String(c.invoicesCount),
        `${fmtMoney(c.revenue)} ${currency}`,
      ]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255 },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Список накладных
  y = addPageIfNeeded(doc, y, 80);
  autoTable(doc, {
    startY: y,
    head: [['№', 'Дата', 'Клиент', 'Позиций', 'Сумма']],
    body: w.invoices.map(inv => [
      String(inv.number),
      fmtDateTime(inv.createdAt),
      inv.clientName,
      String(inv.itemsCount),
      `${fmtMoney(inv.total)} ${currency}`,
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 0: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

function renderTax(doc, t, y, currency) {
  y = blockHeader(doc, 'Налоговый блок', y);
  const rows = [
    ['Валовая выручка (до скидок)', `${fmtMoney2(t.grossRevenue)} ${currency}`],
    ['Скидки',                       `−${fmtMoney2(t.totalDiscount)} ${currency}`],
    ['Чистая выручка (после скидок)',`${fmtMoney2(t.netRevenue)} ${currency}`],
    ['Возвраты от покупателей',      `−${fmtMoney2(t.returnsTotal)} ${currency}  (${t.returnsCount} шт)`],
    ['Итоговая выручка',             `${fmtMoney2(t.finalRevenue)} ${currency}`],
    ['Количество чеков',             String(t.salesCount)],
  ];
  autoTable(doc, {
    startY: y,
    head: [['Показатель', 'Сумма']],
    body: rows,
    theme: 'grid',
    styles: { font: 'Roboto', fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 0: { cellWidth: 110 }, 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 2: Список продаж ────────────────────────────────────
function renderSales(doc, list, y, currency) {
  y = blockHeader(doc, 'Продажи', y);
  autoTable(doc, {
    startY: y,
    head: [['Дата', 'Кассир', 'Товары', 'Скидка', 'Итого']],
    body: list.map(s => [
      fmtDateTime(s.createdAt),
      s.employee || '—',
      (s.itemsSummary || '').slice(0, 60) + ((s.itemsSummary || '').length > 60 ? '…' : ''),
      Number(s.discountAmount) > 0 ? `−${fmtMoney(s.discountAmount)}` : '—',
      `${fmtMoney(s.total)} ${currency}`,
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 25 },
      2: { cellWidth: 95 },
      3: { halign: 'right', cellWidth: 18 },
      4: { halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 3: Скидки ──────────────────────────────────────────
function renderDiscounts(doc, d, y, currency) {
  y = blockHeader(doc, 'Скидки', y);
  doc.setFontSize(10);
  doc.text(
    `Итого скидок: ${fmtMoney(d.total)} ${currency}  ·  ` +
    `на товар: ${fmtMoney(d.totalItemDisc)}  ·  на чек: ${fmtMoney(d.totalSaleDisc)}`,
    14, y
  );
  y += 5;

  if (d.itemDiscounts.length > 0) {
    doc.setFontSize(9);
    doc.text('Скидки на товар:', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Кассир', 'Товар', 'Цена', 'Скидка']],
      body: d.itemDiscounts.slice(0, 50).map(x => [
        fmtDateTime(x.date), x.employee || '—', x.item,
        fmtMoney(x.originalPrice),
        x.discountType === 'percent'
          ? `${x.discountValue} %`
          : `${fmtMoney(x.discountAmount)} ${currency}`,
      ]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  if (d.saleDiscounts.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(9);
    doc.text('Скидки на чек:', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Кассир', 'Сумма до', 'Сумма после', 'Скидка']],
      body: d.saleDiscounts.slice(0, 50).map(x => [
        fmtDateTime(x.date), x.employee || '—',
        fmtMoney(x.subtotalBefore), fmtMoney(x.subtotalAfter),
        x.discountType === 'percent'
          ? `${x.discountValue} %`
          : `${fmtMoney(x.discountAmount)} ${currency}`,
      ]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  if (d.topEmployees && d.topEmployees.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(9);
    doc.text('Топ кассиров по применению скидок:', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [['Кассир', 'Количество скидок']],
      body: d.topEmployees.map(x => [x.name, String(x.count)]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }
  return y + 4;
}

// ─── Блок 4: Возвраты от покупателей ─────────────────────────
function renderReturns(doc, r, y, currency) {
  y = blockHeader(doc, 'Возвраты от покупателей', y);
  doc.setFontSize(10);
  doc.text(`Всего возвратов: ${r.count}  ·  ${fmtMoney(r.total)} ${currency}`, 14, y);
  y += 5;
  if (r.ops.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Возвратов за период нет', 14, y);
    doc.setTextColor(20);
    return y + 8;
  }
  if (r.byReason && r.byReason.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Причина', 'Опер.', 'Сумма']],
      body: r.byReason.map(x => [x.reason, String(x.count), `${fmtMoney(x.money)} ${currency}`]),
      theme: 'plain',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }
  autoTable(doc, {
    startY: y,
    head: [['Дата', 'Товар', 'Кол-во', 'Сумма', 'Причина']],
    body: r.ops.map(op => [
      fmtDateTime(op.date),
      op.product + (op.variantName ? ` · ${op.variantName}` : ''),
      `${fmtNum(op.qty)} ${op.unit || ''}`.trim(),
      `${fmtMoney(op.money)} ${currency}`,
      op.reason || '—',
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 6: Закупки ─────────────────────────────────────────
function renderPurchases(doc, p, y, currency) {
  y = blockHeader(doc, 'Закупки / Приход', y);
  doc.setFontSize(10);
  doc.text(`Всего закупок: ${p.count}  ·  ${fmtMoney(p.total)} ${currency}`, 14, y);
  y += 5;
  if (p.ops.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Закупок за период нет', 14, y);
    doc.setTextColor(20);
    return y + 8;
  }
  if (p.bySupplier && p.bySupplier.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Поставщик', 'Закупок', 'Сумма']],
      body: p.bySupplier.map(x => [x.name, String(x.count), `${fmtMoney(x.money)} ${currency}`]),
      theme: 'plain',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }
  autoTable(doc, {
    startY: y,
    head: [['Дата', 'Товар', 'Кол-во', 'Сумма', 'Примечание']],
    body: p.ops.map(op => [
      fmtDateTime(op.date),
      op.product + (op.variantName ? ` · ${op.variantName}` : ''),
      `${fmtNum(op.qty)} ${op.unit || ''}`.trim(),
      `${fmtMoney(op.money)} ${currency}`,
      op.reason || '—',
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 8: Изъятия (списания) ──────────────────────────────
function renderWriteoffs(doc, w, y, currency) {
  y = blockHeader(doc, 'Изъятия со склада', y);
  doc.setFontSize(10);
  doc.text(`Всего изъятий: ${w.count}  ·  ${fmtMoney(w.total)} ${currency} потерь`, 14, y);
  y += 5;
  if (w.ops.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Изъятий за период нет', 14, y);
    doc.setTextColor(20);
    return y + 8;
  }
  if (w.byReason && w.byReason.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Причина', 'Опер.', 'Сумма потерь']],
      body: w.byReason.map(x => [x.reason, String(x.count), `${fmtMoney(x.money)} ${currency}`]),
      theme: 'plain',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }
  autoTable(doc, {
    startY: y,
    head: [['Дата', 'Товар', 'Кол-во', 'Сумма потерь', 'Причина']],
    body: w.ops.map(op => [
      fmtDateTime(op.date),
      op.product + (op.variantName ? ` · ${op.variantName}` : ''),
      `${fmtNum(op.qty)} ${op.unit || ''}`.trim(),
      `${fmtMoney(op.money)} ${currency}`,
      op.reason || '—',
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [200, 50, 50], textColor: 255 },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 11: Лояльность ─────────────────────────────────────
function renderLoyalty(doc, l, y, currency) {
  y = blockHeader(doc, 'Программа лояльности', y);
  autoTable(doc, {
    startY: y,
    head: [['Показатель', 'Значение']],
    body: [
      ['Начислено баллов', String(l.earned)],
      ['Списано баллов', String(l.redeemed)],
      ['Скидок выдано через баллы', `${fmtMoney(l.discountIssued)} ${currency}`],
    ],
    theme: 'grid',
    styles: { font: 'Roboto', fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 0: { cellWidth: 90 }, 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (l.topCustomers.length > 0) {
    y = addPageIfNeeded(doc, y);
    doc.setFontSize(9);
    doc.text('Топ 10 клиентов по баллам:', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [['№', 'Клиент', 'Баллов', 'Сумма покупок', 'Покупок']],
      body: l.topCustomers.map((c, i) => [
        i + 1, c.name, String(c.points),
        `${fmtMoney(c.totalSpent)} ${currency}`,
        String(c.totalPurchases),
      ]),
      theme: 'striped',
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [240, 240, 240], textColor: 50 },
      columnStyles: { 0: { halign: 'center', cellWidth: 8 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }
  return y + 4;
}

// ─── Блок 14: Покупатели ─────────────────────────────────────
function renderCustomers(doc, c, y, currency) {
  y = blockHeader(doc, 'Покупатели', y);
  doc.setFontSize(10);
  doc.text(`Новых: ${c.newCount}  ·  Вернулись: ${c.returningCount}  ·  Среднее визитов на покупателя: ${c.avgVisitsPerCustomer.toFixed(1)}`, 14, y);
  y += 5;
  if (c.top10.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Покупателей с картой за период нет', 14, y);
    doc.setTextColor(20);
    return y + 8;
  }
  autoTable(doc, {
    startY: y,
    head: [['№', 'Клиент', 'Покупок', 'Сумма', 'Тип']],
    body: c.top10.map((u, i) => [
      i + 1, u.name, String(u.purchases),
      `${fmtMoney(u.totalSpent)} ${currency}`,
      u.isNew ? 'новый' : 'постоянный',
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 0: { halign: 'center', cellWidth: 8 }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Блок 15: Сотрудники ─────────────────────────────────────
function renderCafe(doc, c, y, currency) {
  y = blockHeader(doc, 'Кафе', y);
  doc.setFont('Roboto'); doc.setFontSize(9); doc.setTextColor(60);
  doc.text(
    `Закрыто заказов: ${fmtNum(c.ordersCount)}    Выручка: ${fmtMoney(c.revenue)} ${currency}    Средний чек: ${fmtMoney(c.avgCheck)} ${currency}`,
    14, y,
  );
  y += 6;
  autoTable(doc, {
    startY: y,
    head: [['Официант (взял заказ)', 'Заказов', 'Выручка']],
    body: (c.waiters || []).map(w => [w.name, String(w.count), `${fmtMoney(w.revenue)} ${currency}`]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

function renderEmployees(doc, e, y, currency) {
  y = blockHeader(doc, 'Сотрудники', y);
  autoTable(doc, {
    startY: y,
    head: [['Сотрудник', 'Продаж', 'Выручка', 'Средний чек', 'Скидки']],
    body: e.list.map(emp => [
      emp.name, String(emp.salesCount),
      `${fmtMoney(emp.revenue)} ${currency}`,
      `${fmtMoney(emp.avgCheck)} ${currency}`,
      emp.discountAmount > 0 ? `${fmtMoney(emp.discountAmount)} ${currency}` : '—',
    ]),
    theme: 'striped',
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

// ─── Главная функция ──────────────────────────────────────────
// ─── Денежный поток ──────────────────────────────────────────
function renderCashflow(doc, c, y, currency) {
  y = blockHeader(doc, 'Денежный поток', y);
  const m = (n) => `${fmtMoney(n)} ${currency}`;
  const rows = [
    [{ content: 'ПОСТУПЛЕНИЯ', styles: { fontStyle: 'bold' } }, ''],
    ['Розница (продажи)', m(c.in.retail)],
    ['Оплата от клиентов (опт + долги)', m(c.in.wholesalePaid)],
    [{ content: 'Итого пришло', styles: { fontStyle: 'bold' } }, { content: m(c.in.total), styles: { fontStyle: 'bold' } }],
    [{ content: 'УШЛО', styles: { fontStyle: 'bold' } }, ''],
    ['Закупки', `−${m(c.out.purchases)}`],
    ['Возвраты покупателям', `−${m(c.out.returns)}`],
    ['Списания (потери)', `−${m(c.out.writeoffs)}`],
    [{ content: 'Итого ушло', styles: { fontStyle: 'bold' } }, { content: `−${m(c.out.total)}`, styles: { fontStyle: 'bold' } }],
    [{ content: 'ЧИСТЫЙ ПОТОК', styles: { fontStyle: 'bold' } }, { content: m(c.net), styles: { fontStyle: 'bold' } }],
  ];
  autoTable(doc, {
    startY: y,
    body: rows,
    theme: 'plain',
    styles: { font: 'Roboto', fontSize: 10, cellPadding: 1.8 },
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 5;

  // Справка — это не деньги, а отгрузка/долг
  if (c.info) {
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text('Справочно (не деньги, а отгрузка и долг):', 14, y);
    doc.setTextColor(0);
    y += 2;
    const infoRows = [
      ['Отгружено оптом за период', m(c.info.wholesaleShipped)],
      ['Опт — дебиторка', m(c.info.receivableWholesale || 0)],
      ['Розница в долг — дебиторка', m(c.info.receivableRetail || 0)],
    ];
    autoTable(doc, {
      startY: y,
      body: infoRows,
      theme: 'plain',
      styles: { font: 'Roboto', fontSize: 9, cellPadding: 1.5, textColor: 90 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY;
  }
  return y + 8;
}

// ─── Стоимость склада сейчас ─────────────────────────────────
function renderInventory(doc, inv, y, currency) {
  y = blockHeader(doc, 'Стоимость склада сейчас', y);
  const rows = [
    ['Позиций (активных)', String(inv.productsCount)],
    ['В наличии (остаток > 0)', String(inv.inStockCount)],
    ['Всего единиц на складе', fmtNum(inv.totalUnits)],
    ['Стоимость по себестоимости', `${fmtMoney(inv.valueAtCost)} ${currency}`],
    ['Стоимость в розничных ценах', `${fmtMoney(inv.valueAtRetail)} ${currency}`],
    ['Потенциальная прибыль', `${fmtMoney(inv.potentialProfit)} ${currency}`],
  ];
  autoTable(doc, {
    startY: y,
    body: rows,
    theme: 'plain',
    styles: { font: 'Roboto', fontSize: 10, cellPadding: 2 },
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

export async function generateBusinessReportPDF(data, blocks = []) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // Грузим Roboto для кириллицы
  const fontBase64 = await loadCyrillicFont();
  if (fontBase64) {
    doc.addFileToVFS('Roboto-Regular.ttf', fontBase64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');
  }

  const currency = data.shop?.currency || 'KGS';
  let y = renderHeader(doc, data, currency);

  // Порядок блоков в PDF (даже если запрошены в другом порядке)
  const order = [
    'summary', 'cashflow', 'inventory', 'sales', 'discounts', 'returns',
    'purchases', 'writeoffs', 'loyalty',
    'topProducts', 'categories', 'services', 'customers',
    'employees', 'tax', 'wholesale', 'cafe',
  ];

  for (const block of order) {
    if (!blocks.includes(block)) continue;
    const d = data[block];
    if (!d) continue;

    y = addPageIfNeeded(doc, y, 250);

    if (block === 'summary')      y = renderSummary(doc, d, y, currency);
    else if (block === 'cashflow')    y = renderCashflow(doc, d, y, currency);
    else if (block === 'inventory')   y = renderInventory(doc, d, y, currency);
    else if (block === 'sales')   y = renderSales(doc, d, y, currency);
    else if (block === 'discounts')   y = renderDiscounts(doc, d, y, currency);
    else if (block === 'returns')     y = renderReturns(doc, d, y, currency);
    else if (block === 'purchases')   y = renderPurchases(doc, d, y, currency);
    else if (block === 'writeoffs')   y = renderWriteoffs(doc, d, y, currency);
    else if (block === 'loyalty')     y = renderLoyalty(doc, d, y, currency);
    else if (block === 'topProducts') y = renderTopProducts(doc, d, y, currency);
    else if (block === 'categories')  y = renderCategories(doc, d, y, currency);
    else if (block === 'services')    y = renderServices(doc, d, y, currency);
    else if (block === 'customers')   y = renderCustomers(doc, d, y, currency);
    else if (block === 'employees')   y = renderEmployees(doc, d, y, currency);
    else if (block === 'tax')         y = renderTax(doc, d, y, currency);
    else if (block === 'wholesale')   y = renderWholesale(doc, d, y, currency);
    else if (block === 'cafe')        y = renderCafe(doc, d, y, currency);
  }

  // Нумерация страниц
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(150);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`${i} / ${pageCount}`, 196, 290, { align: 'right' });
  }

  return doc;
}
